/**
 * Application Error Classes
 * Standardized error handling for CaloBite
 */

// ============================================================================
// ERROR CODES
// ============================================================================

export const ERROR_CODES = {
    // Authentication (1xxx)
    AUTH_INVALID_CREDENTIALS: 'ERR_AUTH_1001',
    AUTH_TOKEN_EXPIRED: 'ERR_AUTH_1002',
    AUTH_TOKEN_INVALID: 'ERR_AUTH_1003',
    AUTH_SESSION_EXPIRED: 'ERR_AUTH_1004',
    AUTH_UNAUTHORIZED: 'ERR_AUTH_1005',
    AUTH_RATE_LIMITED: 'ERR_AUTH_1006',
    AUTH_ACCOUNT_LOCKED: 'ERR_AUTH_1007',
    AUTH_2FA_REQUIRED: 'ERR_AUTH_1008',

    // Validation (2xxx)
    VALIDATION_REQUIRED_FIELD: 'ERR_VAL_2001',
    VALIDATION_INVALID_FORMAT: 'ERR_VAL_2002',
    VALIDATION_OUT_OF_RANGE: 'ERR_VAL_2003',
    VALIDATION_INVALID_EMAIL: 'ERR_VAL_2004',
    VALIDATION_INVALID_PHONE: 'ERR_VAL_2005',
    VALIDATION_PASSWORD_WEAK: 'ERR_VAL_2006',
    VALIDATION_DUPLICATE: 'ERR_VAL_2007',

    // Network (3xxx)
    NETWORK_OFFLINE: 'ERR_NET_3001',
    NETWORK_TIMEOUT: 'ERR_NET_3002',
    NETWORK_CONNECTION_FAILED: 'ERR_NET_3003',
    NETWORK_DNS_FAILED: 'ERR_NET_3004',

    // API (4xxx)
    API_REQUEST_FAILED: 'ERR_API_4001',
    API_RESPONSE_INVALID: 'ERR_API_4002',
    API_RATE_LIMITED: 'ERR_API_4003',
    API_SERVICE_UNAVAILABLE: 'ERR_API_4004',
    API_EXTERNAL_FAILURE: 'ERR_API_4005',

    // Data (5xxx)
    DATA_NOT_FOUND: 'ERR_DATA_5001',
    DATA_ALREADY_EXISTS: 'ERR_DATA_5002',
    DATA_INVALID_FORMAT: 'ERR_DATA_5003',
    DATA_SYNC_FAILED: 'ERR_DATA_5004',
    DATA_CONFLICT: 'ERR_DATA_5005',

    // Payment (6xxx)
    PAYMENT_FAILED: 'ERR_PAY_6001',
    PAYMENT_CARD_DECLINED: 'ERR_PAY_6002',
    PAYMENT_INSUFFICIENT_FUNDS: 'ERR_PAY_6003',
    PAYMENT_EXPIRED_CARD: 'ERR_PAY_6004',

    // AI (7xxx)
    AI_REQUEST_FAILED: 'ERR_AI_7001',
    AI_LIMIT_EXCEEDED: 'ERR_AI_7002',
    AI_PROVIDER_UNAVAILABLE: 'ERR_AI_7003',
    AI_INVALID_RESPONSE: 'ERR_AI_7004',

    // Generic (9xxx)
    UNKNOWN: 'ERR_UNKNOWN_9001',
    INTERNAL: 'ERR_INTERNAL_9002'
};

// ============================================================================
// USER-FRIENDLY MESSAGES
// ============================================================================

export const ERROR_MESSAGES = {
    [ERROR_CODES.AUTH_INVALID_CREDENTIALS]: 'Invalid email or password. Please try again.',
    [ERROR_CODES.AUTH_TOKEN_EXPIRED]: 'Your session has expired. Please sign in again.',
    [ERROR_CODES.AUTH_TOKEN_INVALID]: 'Authentication failed. Please sign in again.',
    [ERROR_CODES.AUTH_SESSION_EXPIRED]: 'Your session has expired. Please sign in again.',
    [ERROR_CODES.AUTH_UNAUTHORIZED]: 'You don\'t have permission to perform this action.',
    [ERROR_CODES.AUTH_RATE_LIMITED]: 'Too many attempts. Please wait a moment and try again.',
    [ERROR_CODES.AUTH_ACCOUNT_LOCKED]: 'Your account has been temporarily locked. Please try again later.',
    [ERROR_CODES.AUTH_2FA_REQUIRED]: 'Two-factor authentication is required.',

    [ERROR_CODES.VALIDATION_REQUIRED_FIELD]: 'Please fill in all required fields.',
    [ERROR_CODES.VALIDATION_INVALID_FORMAT]: 'Please check the format of your input.',
    [ERROR_CODES.VALIDATION_OUT_OF_RANGE]: 'Value is outside the allowed range.',
    [ERROR_CODES.VALIDATION_INVALID_EMAIL]: 'Please enter a valid email address.',
    [ERROR_CODES.VALIDATION_INVALID_PHONE]: 'Please enter a valid phone number.',
    [ERROR_CODES.VALIDATION_PASSWORD_WEAK]: 'Password must be at least 8 characters with letters and numbers.',
    [ERROR_CODES.VALIDATION_DUPLICATE]: 'This entry already exists.',

    [ERROR_CODES.NETWORK_OFFLINE]: 'You appear to be offline. Please check your connection.',
    [ERROR_CODES.NETWORK_TIMEOUT]: 'The request timed out. Please try again.',
    [ERROR_CODES.NETWORK_CONNECTION_FAILED]: 'Could not connect to the server. Please try again.',
    [ERROR_CODES.NETWORK_DNS_FAILED]: 'Could not reach the server. Please check your connection.',

    [ERROR_CODES.API_REQUEST_FAILED]: 'Something went wrong. Please try again.',
    [ERROR_CODES.API_RESPONSE_INVALID]: 'Received an unexpected response. Please try again.',
    [ERROR_CODES.API_RATE_LIMITED]: 'Too many requests. Please wait and try again.',
    [ERROR_CODES.API_SERVICE_UNAVAILABLE]: 'Service is temporarily unavailable. Please try again later.',
    [ERROR_CODES.API_EXTERNAL_FAILURE]: 'An external service is currently unavailable.',

    [ERROR_CODES.DATA_NOT_FOUND]: 'The requested item was not found.',
    [ERROR_CODES.DATA_ALREADY_EXISTS]: 'This item already exists.',
    [ERROR_CODES.DATA_INVALID_FORMAT]: 'The data format is invalid.',
    [ERROR_CODES.DATA_SYNC_FAILED]: 'Failed to sync data. Will retry automatically.',
    [ERROR_CODES.DATA_CONFLICT]: 'A conflict occurred. Please refresh and try again.',

    [ERROR_CODES.PAYMENT_FAILED]: 'Payment failed. Please try again.',
    [ERROR_CODES.PAYMENT_CARD_DECLINED]: 'Your card was declined. Please use a different payment method.',
    [ERROR_CODES.PAYMENT_INSUFFICIENT_FUNDS]: 'Insufficient funds. Please use a different payment method.',
    [ERROR_CODES.PAYMENT_EXPIRED_CARD]: 'Your card has expired. Please update your payment method.',

    [ERROR_CODES.AI_REQUEST_FAILED]: 'AI analysis failed. Please try again.',
    [ERROR_CODES.AI_LIMIT_EXCEEDED]: 'You\'ve reached your daily AI limit. Upgrade for more.',
    [ERROR_CODES.AI_PROVIDER_UNAVAILABLE]: 'AI service is temporarily unavailable.',
    [ERROR_CODES.AI_INVALID_RESPONSE]: 'AI returned an unexpected response.',

    [ERROR_CODES.UNKNOWN]: 'An unexpected error occurred. Please try again.',
    [ERROR_CODES.INTERNAL]: 'An internal error occurred. Please try again.'
};

// ============================================================================
// BASE ERROR CLASS
// ============================================================================

export class AppError extends Error {
    constructor(code, message, details = {}) {
        const userMessage = ERROR_MESSAGES[code] || message || ERROR_MESSAGES[ERROR_CODES.UNKNOWN];
        super(userMessage);

        this.name = 'AppError';
        this.code = code || ERROR_CODES.UNKNOWN;
        this.userMessage = userMessage;
        this.technicalMessage = message;
        this.details = details;
        this.timestamp = new Date().toISOString();
        this.isOperational = true; // Expected error vs programming error

        Error.captureStackTrace?.(this, this.constructor);
    }

    toJSON() {
        return {
            name: this.name,
            code: this.code,
            message: this.userMessage,
            technicalMessage: this.technicalMessage,
            details: this.details,
            timestamp: this.timestamp
        };
    }
}

// ============================================================================
// SPECIALIZED ERROR CLASSES
// ============================================================================

export class ValidationError extends AppError {
    constructor(field, message, details = {}) {
        super(ERROR_CODES.VALIDATION_REQUIRED_FIELD, message, { field, ...details });
        this.name = 'ValidationError';
        this.field = field;
    }
}

export class AuthError extends AppError {
    constructor(code = ERROR_CODES.AUTH_UNAUTHORIZED, message, details = {}) {
        super(code, message, details);
        this.name = 'AuthError';
    }
}

export class NetworkError extends AppError {
    constructor(code = ERROR_CODES.NETWORK_OFFLINE, message, details = {}) {
        super(code, message, details);
        this.name = 'NetworkError';
        this.retryable = true;
    }
}

export class APIError extends AppError {
    constructor(code = ERROR_CODES.API_REQUEST_FAILED, message, details = {}) {
        super(code, message, details);
        this.name = 'APIError';
        this.statusCode = details.statusCode;
        this.retryable = [502, 503, 504].includes(details.statusCode);
    }
}

export class DataError extends AppError {
    constructor(code = ERROR_CODES.DATA_NOT_FOUND, message, details = {}) {
        super(code, message, details);
        this.name = 'DataError';
    }
}

export class PaymentError extends AppError {
    constructor(code = ERROR_CODES.PAYMENT_FAILED, message, details = {}) {
        super(code, message, details);
        this.name = 'PaymentError';
    }
}

export class AIError extends AppError {
    constructor(code = ERROR_CODES.AI_REQUEST_FAILED, message, details = {}) {
        super(code, message, details);
        this.name = 'AIError';
        this.retryable = code !== ERROR_CODES.AI_LIMIT_EXCEEDED;
    }
}

// ============================================================================
// ERROR FACTORY
// ============================================================================

export const createError = (type, codeOrField, message, details = {}) => {
    switch (type) {
        case 'validation':
            return new ValidationError(codeOrField, message, details);
        case 'auth':
            return new AuthError(codeOrField, message, details);
        case 'network':
            return new NetworkError(codeOrField, message, details);
        case 'api':
            return new APIError(codeOrField, message, details);
        case 'data':
            return new DataError(codeOrField, message, details);
        case 'payment':
            return new PaymentError(codeOrField, message, details);
        case 'ai':
            return new AIError(codeOrField, message, details);
        default:
            return new AppError(codeOrField, message, details);
    }
};

// ============================================================================
// HELPER: WRAP STANDARD ERRORS
// ============================================================================

export const wrapError = (error) => {
    if (error instanceof AppError) {
        return error;
    }

    // Network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return new NetworkError(ERROR_CODES.NETWORK_CONNECTION_FAILED, error.message);
    }

    // Timeout
    if (error.name === 'AbortError' || error.message?.includes('timeout')) {
        return new NetworkError(ERROR_CODES.NETWORK_TIMEOUT, error.message);
    }

    // Generic
    return new AppError(ERROR_CODES.UNKNOWN, error.message, { originalError: error.name });
};

export default {
    ERROR_CODES,
    ERROR_MESSAGES,
    AppError,
    ValidationError,
    AuthError,
    NetworkError,
    APIError,
    DataError,
    PaymentError,
    AIError,
    createError,
    wrapError
};
